import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { useMemo, useState } from "react";
import type {
  DocAccessCategoryRow,
  DocAccessDocumentRow,
  DocAccessEffect,
  DocAccessResellerRule,
  DocAccessTree,
} from "@xop/dashboard-shared";

import { VerdictPill } from "./VerdictPill.js";
import { ruleKey, type LocalVerdict, type RuleDraft } from "./verdict.js";

/**
 * The library, as this one account sees it.
 *
 * Every row answers two questions at once: what happens today (the pill),
 * and what staff have decided (the Auto / Allow / Block control). Keeping
 * them side by side is the whole design — the previous arrangement made
 * staff flip a folder toggle and then go and look at the customer portal to
 * find out what they had done.
 *
 * "Auto" is not a third policy. It is the absence of an override, and it
 * says so: the row falls back to whatever the products and the folder
 * publication decide, and the pill shows which. Choosing Allow or Block is
 * the act of overruling that, and it is the only thing stored.
 *
 * A RESELLER has no Auto (2026-09-15): its documentation is blocked by
 * default, so the control is just Allow / Block — green and red when chosen
 * — and clicking the chosen one again clears it back to blocked. A customer
 * keeps Auto. `allowAuto` is the switch.
 *
 * Folders carry the same control and cascade to everything beneath them at
 * any depth — but a document inside can still overrule its folder, which is
 * exactly the "block Webcast except this one datasheet" case that had no
 * expression at all before.
 *
 * A CUSTOMER's reseller can share or hide documents for it too (2026-09-30).
 * Those rules are the reseller's, not staff's, so the row does not put them
 * in the control — it wears a small "Reseller: Shared / Hidden" chip instead,
 * and a staff Allow or Block on the same row still wins.
 */
export function AccessTree({
  tree,
  verdicts,
  draft,
  onSetRule,
  showHiddenOnly,
  allowAuto = true,
}: {
  tree: DocAccessTree;
  verdicts: Map<number, LocalVerdict>;
  draft: RuleDraft;
  onSetRule: (
    targetType: "Document" | "DocumentCategory",
    targetId: number,
    effect: DocAccessEffect | null,
  ) => void;
  showHiddenOnly: boolean;
  /** False for a reseller: no Auto, blocked unless allowed. */
  allowAuto?: boolean;
}): JSX.Element {
  const { roots, childrenOf, docsByCategory, unfiled } = useMemo(() => {
    const childMap = new Map<number | null, DocAccessCategoryRow[]>();
    for (const category of tree.categories) {
      const key = category.parent_id;
      const list = childMap.get(key) ?? [];
      list.push(category);
      childMap.set(key, list);
    }

    const docMap = new Map<number, DocAccessDocumentRow[]>();
    const loose: DocAccessDocumentRow[] = [];
    for (const doc of tree.documents) {
      if (doc.category_id == null) {
        loose.push(doc);
        continue;
      }
      const list = docMap.get(doc.category_id) ?? [];
      list.push(doc);
      docMap.set(doc.category_id, list);
    }

    return {
      roots: childMap.get(null) ?? [],
      childrenOf: childMap,
      docsByCategory: docMap,
      unfiled: loose,
    };
  }, [tree]);

  if (tree.categories.length === 0 && tree.documents.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        The library is empty — there is nothing to grant yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5} data-testid="access-tree">
      {roots.map((category) => (
        <CategoryBranch
          key={category.id}
          category={category}
          depth={0}
          childrenOf={childrenOf}
          docsByCategory={docsByCategory}
          verdicts={verdicts}
          draft={draft}
          onSetRule={onSetRule}
          showHiddenOnly={showHiddenOnly}
          allowAuto={allowAuto}
        />
      ))}

      {unfiled.length > 0 ? (
        <Box sx={{ pt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
            Not filed in a folder
          </Typography>
          {unfiled.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              depth={1}
              verdict={verdicts.get(doc.id)}
              draft={draft}
              onSetRule={onSetRule}
              showHiddenOnly={showHiddenOnly}
              allowAuto={allowAuto}
            />
          ))}
        </Box>
      ) : null}
    </Stack>
  );
}

function CategoryBranch({
  category,
  depth,
  childrenOf,
  docsByCategory,
  verdicts,
  draft,
  onSetRule,
  showHiddenOnly,
  allowAuto,
}: {
  category: DocAccessCategoryRow;
  depth: number;
  childrenOf: Map<number | null, DocAccessCategoryRow[]>;
  docsByCategory: Map<number, DocAccessDocumentRow[]>;
  verdicts: Map<number, LocalVerdict>;
  draft: RuleDraft;
  onSetRule: (
    targetType: "Document" | "DocumentCategory",
    targetId: number,
    effect: DocAccessEffect | null,
  ) => void;
  showHiddenOnly: boolean;
  allowAuto: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(depth === 0);

  const docs = docsByCategory.get(category.id) ?? [];
  const subcategories = childrenOf.get(category.id) ?? [];

  const visibleCount = docs.filter((d) => verdicts.get(d.id)?.visible).length;
  const current = draftEffect(draft, category.rule?.effect ?? null, "DocumentCategory", category.id);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pl: depth * 2.5,
          py: 0.5,
          borderRadius: 1,
          bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5),
        }}
        data-testid={`access-category-${category.id}`}
      >
        <IconButton size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? "Collapse" : "Expand"}>
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>

        <FolderOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />

        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1, minWidth: 0 }} noWrap>
          {category.name}
        </Typography>

        {/*
          A folder that is not published to this portal is a LIBRARY decision,
          and it outranks anything decided per-account — so the row says so
          plainly and the control is disabled rather than silently ineffective.
          Offering an "Allow" that cannot work is how an access UI teaches
          people to distrust it.
        */}
        <ResellerRuleChip
          rule={category.reseller_rule ?? null}
          testId={`access-category-reseller-rule-${category.id}`}
        />

        {category.published_to_portal ? (
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {visibleCount} of {docs.length} visible
          </Typography>
        ) : (
          <Tooltip title="Publish this folder to the portal in the folder settings first. An account-level rule cannot open an unpublished folder.">
            <Chip
              size="small"
              label="Folder not published"
              variant="outlined"
              sx={{ height: 20, fontSize: 11 }}
            />
          </Tooltip>
        )}

        <RuleControl
          value={current}
          // A stored rule stays clearable even where it cannot take effect.
          disabled={!category.published_to_portal && current == null}
          allowAuto={allowAuto}
          onChange={(effect) => onSetRule("DocumentCategory", category.id, effect)}
          testId={`access-category-control-${category.id}`}
        />
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box>
          {subcategories.map((child) => (
            <CategoryBranch
              key={child.id}
              category={child}
              depth={depth + 1}
              childrenOf={childrenOf}
              docsByCategory={docsByCategory}
              verdicts={verdicts}
              draft={draft}
              onSetRule={onSetRule}
              showHiddenOnly={showHiddenOnly}
              allowAuto={allowAuto}
            />
          ))}

          {docs.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              depth={depth + 1}
              verdict={verdicts.get(doc.id)}
              draft={draft}
              onSetRule={onSetRule}
              showHiddenOnly={showHiddenOnly}
              allowAuto={allowAuto}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function DocumentRow({
  doc,
  depth,
  verdict,
  draft,
  onSetRule,
  showHiddenOnly,
  allowAuto,
}: {
  doc: DocAccessDocumentRow;
  depth: number;
  verdict: LocalVerdict | undefined;
  draft: RuleDraft;
  onSetRule: (
    targetType: "Document" | "DocumentCategory",
    targetId: number,
    effect: DocAccessEffect | null,
  ) => void;
  showHiddenOnly: boolean;
  allowAuto: boolean;
}): JSX.Element | null {
  const resolved: LocalVerdict = verdict ?? {
    visible: doc.visible,
    reason: doc.reason,
    detail: doc.reason_detail,
  };

  if (showHiddenOnly && resolved.visible) return null;

  const current = draftEffect(draft, doc.rule?.effect ?? null, "Document", doc.id);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: depth * 2.5 + 4.5,
        py: 0.5,
        borderRadius: 1,
        opacity: resolved.visible ? 1 : 0.75,
        "&:hover": { bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4) },
      }}
      data-testid={`access-document-${doc.id}`}
    >
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={doc.title}>
        {doc.title}
      </Typography>

      {doc.default_release_version ? (
        // Release documentation: its audience follows the release, whatever
        // folder it sits in — say so beside the title.
        <Chip
          size="small"
          label={allowAuto ? `v${doc.default_release_version} · automatic` : `v${doc.default_release_version} · release guide`}
          variant="outlined"
          color="primary"
          data-testid={`access-document-release-${doc.id}`}
          sx={{ height: 18, fontSize: 11 }}
        />
      ) : null}

      {doc.access_scope !== "general" ? (
        <Chip
          size="small"
          label={doc.access_scope}
          variant="outlined"
          sx={{ height: 18, fontSize: 11, textTransform: "capitalize" }}
        />
      ) : null}

      <ResellerRuleChip rule={doc.reseller_rule ?? null} testId={`access-document-reseller-rule-${doc.id}`} />

      <VerdictPill verdict={resolved} />

      <RuleControl
        value={current}
        // The library outranks every account rule, so an Allow that cannot
        // work is not offered (a stored rule stays clearable).
        disabled={LIBRARY_REASONS.has(resolved.reason) && current == null}
        allowAuto={allowAuto}
        onChange={(effect) => onSetRule("Document", doc.id, effect)}
        testId={`access-document-control-${doc.id}`}
      />
    </Box>
  );
}

/**
 * What the customer's reseller decided on this exact row, read-only. It is a
 * marker, not a verdict: whether it still takes effect is the pill's job
 * (a staff rule outranks it, and it only counts while the reseller can read
 * the document itself).
 */
function ResellerRuleChip({
  rule,
  testId,
}: {
  rule: DocAccessResellerRule | null;
  testId: string;
}): JSX.Element | null {
  if (!rule) return null;
  const shared = rule.effect === "allow";

  return (
    <Tooltip
      title={
        shared
          ? `${rule.reseller_name} shared this with the customer. A rule of yours on this row wins.`
          : `${rule.reseller_name} hid this from the customer. A rule of yours on this row wins.`
      }
    >
      <Chip
        size="small"
        variant="outlined"
        color={shared ? "success" : "error"}
        label={shared ? "Reseller: Shared" : "Reseller: Hidden"}
        data-testid={testId}
        data-effect={rule.effect}
        sx={{ height: 18, fontSize: 11 }}
      />
    </Tooltip>
  );
}

/** Verdicts decided by the LIBRARY layer — no account rule can change them. */
const LIBRARY_REASONS = new Set<string>(["unpublished", "folder_unpublished", "uncategorized"]);

/**
 * Auto / Allow / Block, or — for a reseller — Allow / Block. `null` is "no
 * rule" and stores nothing: Auto for a customer, blocked for a reseller.
 * The chosen effect wears its meaning: Allow green, Block red.
 */
function RuleControl({
  value,
  disabled,
  allowAuto,
  onChange,
  testId,
}: {
  value: DocAccessEffect | null;
  disabled: boolean;
  allowAuto: boolean;
  onChange: (effect: DocAccessEffect | null) => void;
  testId: string;
}): JSX.Element {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value ?? (allowAuto ? "auto" : null)}
      disabled={disabled}
      onChange={(_, next: string | null) => {
        if (next == null) {
          // Clicking the chosen button again. With Auto on offer that would
          // leave nothing chosen, so it is ignored; without it, it is how a
          // rule is cleared back to "blocked by default".
          if (!allowAuto) onChange(null);
          return;
        }
        onChange(next === "auto" ? null : (next as DocAccessEffect));
      }}
      data-testid={testId}
      sx={{
        "& .MuiToggleButton-root": { px: 1, py: 0.25, fontSize: 11, textTransform: "none" },
        "& .MuiToggleButton-root.Mui-selected[value='allow']": {
          bgcolor: "success.main",
          color: "success.contrastText",
          "&:hover": { bgcolor: "success.dark" },
        },
        "& .MuiToggleButton-root.Mui-selected[value='deny']": {
          bgcolor: "error.main",
          color: "error.contrastText",
          "&:hover": { bgcolor: "error.dark" },
        },
      }}
    >
      {allowAuto ? <ToggleButton value="auto">Auto</ToggleButton> : null}
      <ToggleButton value="allow">Allow</ToggleButton>
      <ToggleButton value="deny">Block</ToggleButton>
    </ToggleButtonGroup>
  );
}

function draftEffect(
  draft: RuleDraft,
  stored: DocAccessEffect | null,
  targetType: "Document" | "DocumentCategory",
  targetId: number,
): DocAccessEffect | null {
  const key = ruleKey(targetType, targetId);
  if (draft.has(key)) return draft.get(key) ?? null;
  return stored;
}
